import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Card from "../components/Card";
import Shop from "../components/Shop";
import Draft from "../components/Draft";
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, setDoc, getDoc, updateDoc, increment, arrayUnion } from "firebase/firestore";
import confetti from "canvas-confetti";

// ==========================================
// CLOUD & LOCAL TRACKING
// ==========================================
export async function trackStat(statKey, amount = 1) {
  const currentStats = JSON.parse(localStorage.getItem("unlocked_stats")) || {};
  currentStats[statKey] = (currentStats[statKey] || 0) + amount;
  localStorage.setItem("unlocked_stats", JSON.stringify(currentStats));

  const user = auth.currentUser;
  if (user) {
    const userRef = doc(db, "users", user.uid);
    try {
      await updateDoc(userRef, { [statKey]: increment(amount) });
    } catch (error) {
      if (error.code === 'not-found') await setDoc(userRef, { [statKey]: amount, displayName: user.displayName });
    }
  }
}

export async function unlockCard(card) {
  if (!card || !card.name) return;
  if (card.type !== "special" && card.type !== "effect") return;
  const cardName = card.name;

  const currentUnlocked = JSON.parse(localStorage.getItem("unlocked_cards")) || [];
  if (!currentUnlocked.includes(cardName)) {
    currentUnlocked.push(cardName);
    localStorage.setItem("unlocked_cards", JSON.stringify(currentUnlocked));
  }

  const user = auth.currentUser;
  if (user) {
    const userRef = doc(db, "users", user.uid);
    try {
      await updateDoc(userRef, { discoveredCards: arrayUnion(cardName) });
    } catch (error) {
      if (error.code === 'not-found') await setDoc(userRef, { discoveredCards: [cardName], displayName: user.displayName });
    }
  }
}

export default function HostGame({ socket, setIsGameActive }) {
  // ==========================================
  // 1. STATES & REFS
  // ==========================================
  
  // Ansicht & Lobby
  const [appView, setAppView] = useState("setup"); 
  const [myName, setMyName] = useState("");
  const [lobbyPlayers, setLobbyPlayers] = useState([]);
  const [currentShop, setCurrentShop] = useState(null);
  
  // NEU: Fehler-States für das Beitreten
  const [joinError, setJoinError] = useState("");
  const [isShakingJoin, setIsShakingJoin] = useState(false);

  // Multiplayer Server-States
  const [gamePhase, setGamePhase] = useState("lobby"); 
  const [playersReady, setPlayersReady] = useState([]);
  const [players, setPlayers] = useState([]); 
  const [topCard, setTopCard] = useState(null);
  const [cardHistory, setCardHistory] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [activePenalty, setActivePenalty] = useState(0);
  const [direction, setDirection] = useState(1);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  const [totalRounds, setTotalRounds] = useState(5);
  const [lobbyTotalRounds, setLobbyTotalRounds] = useState(25); 
  const [lobbyMaxPlayers, setLobbyMaxPlayers] = useState(5);
  const [roomCode, setRoomCode] = useState(""); 
  const [joinInput, setJoinInput] = useState(""); 
  const [spyData, setSpyData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const myPlayerRef = useRef(null); 
  const [globalOnlineUsers, setGlobalOnlineUsers] = useState({});
  const [myFriends, setMyFriends] = useState([]);

  // UI, Overlays & Game Feel
  const [actionLog, setActionLog] = useState([]);
  const [screenShake, setScreenShake] = useState(false);
  const [screenFlash, setScreenFlash] = useState(null); 
  const [showDeck, setShowDeck] = useState(false);
  const [chooseColorMode, setChooseColorMode] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [openedPackage, setOpenedPackage] = useState(null);
  const [showEffects, setShowEffects] = useState(false);
  const [showSniperOverlay, setShowSniperOverlay] = useState(false);
  const [showWishOverlay, setShowWishOverlay] = useState(false);
  const [wishColor, setWishColor] = useState("red");
  const [wishNumber, setWishNumber] = useState(0);
  const [sortBy, setSortBy] = useState("none");
  const [animatingCardIndex, setAnimatingCardIndex] = useState(null);
  const [animationType, setAnimationType] = useState(""); 
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [chatBubbles, setChatBubbles] = useState({}); 
  const [showEmoteMenu, setShowEmoteMenu] = useState(false);
  const [topSpecialCard, setTopSpecialCard] = useState(null);
  const [recycleMode, setRecycleMode] = useState(false);
  const [scryCards, setScryCards] = useState(null);
  const [spyTargetEffectId, setSpyTargetEffectId] = useState(null);
  const [sellEffectConfirm, setSellEffectConfirm] = useState(null);

  const availableEmotes = ["Hello!", "Oops...", "Aua!", "😂", "😡", "GG"];

  const currentRoundRef = useRef(currentRound);
  const prevHpRef = useRef(20);
  const prevGoldRef = useRef(0);

  const activePlayer = players[currentPlayerIndex];
  const mySessionId = sessionStorage.getItem("unlocked_session");
  
  // Finde den Spieler über die Socket-ID ODER die persistente Session-ID
  const myPlayer = players.find(p => p.id === socket.id || (mySessionId && p.sessionId === mySessionId));
  const myPlayerIndex = players.findIndex(p => p.id === socket.id || (mySessionId && p.sessionId === mySessionId));

  useEffect(() => { myPlayerRef.current = myPlayer; }, [myPlayer]);
  useEffect(() => { currentRoundRef.current = currentRound; }, [currentRound]);

  // Tab-Titel
  useEffect(() => {
    let title = "UN-Locked";
    if (appView === "lobby_waiting") title = `UN-Locked | Lobby (${roomCode})`;
    else if (appView === "game") {
      if (gamePhase === "shop") title = "UN-Locked | 🛒 Shop";
      else if (gamePhase === "battle") title = "UN-Locked | ⚔️ Battle";
      else if (gamePhase === "start_draft") title = "UN-Locked | 🔮 Draft";
      else if (gamePhase === "game_over") title = "UN-Locked | 🏆 Game Over";
    }
    document.title = title;
  }, [appView, gamePhase, roomCode]);

  // ==========================================
  // 2. SERVER KOMMUNIKATION
  // ==========================================
  const [searchParams] = useSearchParams();
  const hasTriggeredBot = useRef(false);
  
  useEffect(() => {
    // 1. Schauen, ob wir per Link einem Raum beitreten
    const urlCode = searchParams.get("code");
    if (urlCode) setJoinInput(urlCode.toUpperCase());

    // 2. Schauen, ob wir von der Startseite kommen und sofort spielen wollen
    const action = searchParams.get("action");
    if (action === "bot" && socket && !hasTriggeredBot.current) {
      hasTriggeredBot.current = true; // Verhindert doppelten Start
      const finalName = myName.trim() || `Player ${Math.floor(Math.random() * 1000)}`;
      socket.emit("create_bot_game", finalName);
      
      // Die URL wieder sauber machen, damit es beim Neuladen nicht nochmal triggert
      window.history.replaceState({}, document.title, "/host");
    }
  }, [searchParams, socket, myName]);

  useEffect(() => {
    if (!socket) return;

    const handleReconnect = () => {
      const savedSession = sessionStorage.getItem("unlocked_session");
      const savedRoom = sessionStorage.getItem("unlocked_room");
      if (savedSession && savedRoom) {
        socket.emit("reconnect_user", { roomCode: savedRoom, sessionId: savedSession });
        setRoomCode(savedRoom);
      }
    };

    // 1. Beim ersten Rendern ausführen, falls der Socket schon steht
    if (socket.connected) handleReconnect();

    // 2. WICHTIG: Den Server auch bei jedem stillen Reconnect im Hintergrund informieren!
    socket.on("connect", handleReconnect);

    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [socket]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user && user.displayName) {
        setMyName(user.displayName); 
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().friends) setMyFriends(docSnap.data().friends);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (topCard) {
      setCardHistory(prev => {
        // Wenn schon Karten liegen und die ID die gleiche ist...
        if (prev.length > 0 && prev[prev.length - 1].id === topCard.id) {
          // ...ABER die Farbe sich geändert hat (z.B. durch Farbwahl/Wish), dann aktualisieren wir sie!
          if (prev[prev.length - 1].color !== topCard.color) {
            const newHistory = [...prev];
            newHistory[newHistory.length - 1] = topCard;
            return newHistory;
          }
          return prev; // Ansonsten ignorieren, um doppeltes Einfügen zu verhindern
        }
        return [...prev, topCard].slice(-3); // Behält immer maximal die letzten 3
      });
    }
  }, [topCard]);

  useEffect(() => {
    if (!socket) return;

    socket.on("online_users_update", setGlobalOnlineUsers);
    socket.on("lobby_update", setLobbyPlayers);

    // NEU: Fehler beim Beitreten
    socket.on("join_error", (msg) => {
      setJoinError(msg || "Room not found!");
      setIsShakingJoin(true);
      setTimeout(() => setIsShakingJoin(false), 500);
    });

    socket.on("room_created", (code) => { 
      setJoinError(""); setRoomCode(code); setAppView("lobby_waiting"); 
      if (currentUser) socket.emit("set_status", { uid: currentUser.uid, status: "In Game" });
    });
    
    socket.on("room_joined", (code) => { 
      setJoinError(""); setRoomCode(code); setAppView("lobby_waiting"); 
      if (currentUser) socket.emit("set_status", { uid: currentUser.uid, status: "In Game" });
    });

    socket.on("session_info", (data) => {
      sessionStorage.setItem("unlocked_session", data.sessionId);
      sessionStorage.setItem("unlocked_room", data.roomCode);
    });

    socket.on("game_state_update", (newState) => {
      if (newState.currentRound) setCurrentRound(newState.currentRound);
      if (newState.totalRounds) setTotalRounds(newState.totalRounds);
      setPlayers(newState.players);
      setTopCard(newState.topCard);
      setCurrentPlayerIndex(newState.currentPlayerIndex);
      setActivePenalty(newState.activePenalty);
      setDirection(newState.direction);
      setHasDrawn(newState.hasDrawn);
      setTopSpecialCard(newState.topSpecialCard);
      
      if (newState.phase) {
        setGamePhase(prevPhase => {
          if (prevPhase !== "shop" && newState.phase === "shop") setOpenedPackage(null); 
          return newState.phase;
        });
      }
      
      setCurrentShop(newState.currentShop || null);
      if (newState.playersReady) setPlayersReady(newState.playersReady);
      if (newState.phase === "battle") setAppView(prev => prev !== "game" ? "game" : prev); 
    });

    
    socket.on("scry_options", (cards) => setScryCards(cards));

    const handleEmote = ({ playerIndex, emote }) => {
      setChatBubbles(prev => ({ ...prev, [playerIndex]: emote }));
      setTimeout(() => {
        setChatBubbles(prev => {
          const newBubbles = { ...prev };
          delete newBubbles[playerIndex];
          return newBubbles;
        });
      }, 3500);
    };

    socket.on("receive_emote", handleEmote);
    socket.on("send_emote", handleEmote);

    socket.on("game_started", () => setAppView("game"));
    socket.on("request_color_choice", () => setChooseColorMode(true));

    socket.on("round_winner_stats", ({ winnerId, earnedGold }) => {
      if (myPlayerRef.current && myPlayerRef.current.id === winnerId) {
        trackStat("wins", 1); trackStat("totalGold", earnedGold);
        trackStat("gamesPlayed", 1); trackStat("doubloons", 1);
      }
    });
    
    socket.on("server_message", (msg) => {
      setActionLog(prev => [{ text: msg, round: currentRoundRef.current }, ...prev].slice(0, 20));

      const lowerMsg = msg.toLowerCase();

      // 🛡️ SHIELD EFFEKT (Blaue Aura)
      // Passe das Wort "shield" an, falls dein Server etwas anderes in den Chat schreibt!
      if (lowerMsg.includes("shield") || lowerMsg.includes("protect")) {
        confetti({
          particleCount: 100,
          spread: 360, // Schießt in alle Richtungen (wie eine Kuppel)
          origin: { y: 0.5 },
          colors: ['#00bfff', '#ffffff', '#00ffff'], // Cyan und Weiß
          shapes: ['circle'],
          gravity: 0.2, // Fast keine Schwerkraft, schwebt im Raum
          decay: 0.94, // Verpufft sehr schnell (wie Energie)
          scalar: 0.8,
          startVelocity: 30
        });
      }

      // 🎯 FIRST STRIKE (Roter Impact)
      if (lowerMsg.includes("first strike")) {
        confetti({
          particleCount: 100, spread: 160, origin: { y: 0.5 },
          colors: ['#ff0000', '#8b0000', '#000000'],
          shapes: ['circle'], // Keine Papier-Schnipsel mehr
          gravity: 2, scalar: 1.2, startVelocity: 45
        });
        setScreenShake(true);
        setTimeout(() => setScreenShake(false), 500);
      }

      // 💣 BOMB (Massive dunkle Explosion)
      if (lowerMsg.includes("bomb")) {
        confetti({
          particleCount: 180, spread: 360, origin: { y: 0.5 },
          colors: ['#333333', '#ff4400', '#000000', '#ff8800'],
          shapes: ['circle', 'square'], // Hier mischen wir für den Trümmer-Look
          gravity: 1.5, scalar: 1.5, startVelocity: 55, ticks: 150
        });
        setScreenShake(true);
        setTimeout(() => setScreenShake(false), 800);
      }
      
      // 🏆 WIN ROUND (Goldregen)
      if (lowerMsg.includes("wins round")) {
        confetti({
          particleCount: 200, spread: 100, origin: { y: 0.2 }, // Spawnt weiter oben!
          colors: ['#ffd700', '#ffffff'],
          shapes: ['star', 'circle'],
          gravity: 1, ticks: 300
        });
      }
    });

    socket.on("request_sniper_target", () => setShowSniperOverlay(true));
    socket.on("request_wish_choice", () => { setWishColor("red"); setShowWishOverlay(true); });
    socket.on("spy_result", setSpyData);
    socket.on("round_over", () => setGameOver(true));

    return () => {
      socket.off("lobby_update"); socket.off("game_state_update"); socket.off("game_started");
      socket.off("request_color_choice"); socket.off("server_message"); socket.off("round_over");
      socket.off("request_sniper_target"); socket.off("request_wish_choice"); socket.off("spy_result");
      socket.off("online_users_update"); socket.off("receive_emote"); socket.off("join_error"); socket.off("send_emote");
    };
  }, [socket, currentUser]);

  useEffect(() => {
    if (setIsGameActive) setIsGameActive(appView !== "setup");
    return () => { if (setIsGameActive) setIsGameActive(false); }
  }, [appView, setIsGameActive]);

  // ==========================================
  // 3. LOGIK & HILFSFUNKTIONEN
  // ==========================================
  useEffect(() => {
    if (gamePhase === "game_over" && myPlayer) {
      const sessionKey = `recorded_game_${roomCode}`;
      if (!sessionStorage.getItem(sessionKey)) {
        trackStat("gamesPlayed", 1); trackStat("totalGold", myPlayer.cumulativeGold || 0); 
        sessionStorage.setItem(sessionKey, "true");
      }
    }
  }, [gamePhase, myPlayer, roomCode]);

  useEffect(() => {
    if (!myPlayer) return;
    
    // SCHADEN KASSIERT
    if (myPlayer.hp < prevHpRef.current) {
      const dmg = prevHpRef.current - myPlayer.hp;
      
      // Nur Partikel & Wackeln, wenn es ein "echter" Hit oder eine Strafe ist (> 1 DMG)
      if (dmg > 1) {
        confetti({ 
          particleCount: 20 * dmg, // Je mehr Schaden, desto wuchtiger!
          spread: 80, 
          origin: { y: 0.7 }, 
          colors: ['#ff0000', '#8b0000', '#440000'], // Dunkleres Blutrot
          shapes: ['circle'], // Kreise statt Papier!
          gravity: 2.5, // Fällt schwerer nach unten
          scalar: 1.2,
          ticks: 80 
        });
        setScreenShake(true); 
      }
      
      // Der rote Bildschirm-Flash kommt aber immer (auch beim 1 HP Ziehen)
      setScreenFlash("damage");
      setTimeout(() => { setScreenShake(false); setScreenFlash(null); }, 400);
    } 
    // GEHEILT (Magische grüne Funken)
    else if (myPlayer.hp > prevHpRef.current) {
      confetti({ 
        particleCount: 50, spread: 60, origin: { y: 0.7 }, 
        colors: ['#00ff00', '#32cd32', '#ffffff'], 
        shapes: ['star', 'circle'], // Sterne und Kreise
        gravity: 0.8 // Schwebt leicht
      });
      setScreenFlash("heal"); setTimeout(() => setScreenFlash(null), 400);
    }
    
    // GOLD VERDIENT (Schwere Goldmünzen)
    if (myPlayer.gold > prevGoldRef.current) {
      if (myPlayer.gold - prevGoldRef.current > 2) {
        confetti({ 
          particleCount: 40, spread: 50, origin: { y: 0.7 }, 
          colors: ['#ffd700', '#ffa500'], 
          shapes: ['circle'], // Sieht aus wie kleine Münzen
          gravity: 2, scalar: 1.2
        });
      }
      setScreenFlash("gold"); setTimeout(() => setScreenFlash(null), 400);
    }
    
    prevHpRef.current = myPlayer.hp; prevGoldRef.current = myPlayer.gold;
  }, [myPlayer?.hp, myPlayer?.gold]);

  const checkIfPlayable = (card) => {
    if (!topCard) return true;
    if (card.color === "black" || card.type === "wild") return true;
    if (card.color === topCard.color) return true;
    if (card.value !== null && card.value !== undefined && card.value === topCard.value) return true;
    if (card.name && card.name === topCard.name) return true;
    if (myPlayer && myPlayer.activeEffects) {
      if (myPlayer.activeEffects.some(e => e.name === "Color Blind")) {
        const isRedYellow = card.color === "red" && topCard.color === "yellow";
        const isYellowRed = card.color === "yellow" && topCard.color === "red";
        if (isRedYellow || isYellowRed) return true;
      }
    }
    return false;
  };

  function playCard(card, realIndex) {
    if (players[currentPlayerIndex].id !== socket.id) return;
    socket.emit("play_card", { roomCode, card, playerIndex: currentPlayerIndex, cardIndex: realIndex });
  }

  function drawCard() {
    if (players[currentPlayerIndex].id !== socket.id) return;
    if (activePenalty > 0) trackStat("penaltiesTaken", activePenalty);
    socket.emit("draw_card", { roomCode, playerIndex: currentPlayerIndex });
  }

  function endTurn() {
    if (players[currentPlayerIndex].id !== socket.id) return;
    socket.emit("end_turn", { roomCode, playerIndex: currentPlayerIndex });
  }

  // ==========================================
  // 4. RENDER VIEWS (AUSGELAGERT FÜR ÜBERSICHT)
  // ==========================================
  
  const renderDisconnectOverlay = () => {
    const offlinePlayers = players ? players.filter(p => p.connected === false) : [];
    if (offlinePlayers.length === 0 || appView === "setup" || appView === "lobby_waiting" || gamePhase === "game_over") return null;
    return (
      <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", zIndex: 9999, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", color: "white", textAlign: "center" }}>
        <h1 style={{ color: "gold", fontSize: "4em", animation: "pulse 1.5s infinite" }}>Waiting for Reconnect... 🔌</h1>
        <h2 style={{ fontSize: "2.5em", marginBottom: 10 }}>{offlinePlayers.map(p => p.name).join(", ")} lost the Connection.</h2>
        <p style={{ color: "#aaa", fontSize: "1.5em", maxWidth: "600px" }}>Game paused. The game will be canceled if the player doesnt reconnect in the next 60 seconds.</p>
        <button onClick={() => { trackStat("doubloons", 1); setShowLeaveConfirm(true); }} style={{ marginTop: "30px", padding: "15px 30px", fontSize: "1.2em", backgroundColor: "darkred", color: "white", border: "2px solid red", borderRadius: "10px", cursor: "pointer", fontWeight: "bold" }}>Leave Game 🚪</button>
      </div>
    );
  };

  const renderLeaveConfirm = () => {
    if (!showLeaveConfirm) return null;
    return (
      <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.8)", zIndex: 10000, display: "flex", justifyContent: "center", alignItems: "center", backdropFilter: "blur(5px)" }}>
        <div style={{ backgroundColor: "#222", padding: "40px", borderRadius: "15px", border: "2px solid gold", textAlign: "center", color: "white", boxShadow: "0 10px 30px rgba(0,0,0,0.8)" }}>
          <h2 style={{ color: "gold", marginTop: 0 }}>Leave Game?</h2>
          <p style={{ color: "#aaa", marginBottom: "30px" }}>Return to Main Menu?</p>
          <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
            <button onClick={() => { sessionStorage.removeItem("unlocked_session"); sessionStorage.removeItem("unlocked_room"); window.location.href = "/"; }} style={{ padding: "10px 20px", backgroundColor: "darkred", color: "white", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", fontSize: "1.1em" }}>Yes</button>
            <button onClick={() => setShowLeaveConfirm(false)} style={{ padding: "10px 20px", backgroundColor: "#555", color: "white", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", fontSize: "1.1em" }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  const renderSetup = () => (
    <div style={{ paddingTop: "80px", paddingBottom: "40px", paddingLeft: "20px", paddingRight: "20px", color: "white", minHeight: "100vh", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <Helmet><title>Join or Host Game | UN-LOCKED</title></Helmet>
      <style>{` button { transition: transform 0.1s ease-in-out, filter 0.1s ease-in-out, box-shadow 0.1s ease-in-out; } button:hover { transform: scale(1.05); filter: brightness(1.2); } .shake-join { animation: shake-anim 0.4s cubic-bezier(.36,.07,.19,.97) both; border-color: red !important; } `}</style>
      
      <h1 style={{ color: "white", fontSize: "5em", margin: "0 0 30px 0", textShadow: "0 5px 15px rgba(0,0,0,0.8)", fontWeight: "900" }}>UN-LOCKED</h1>
      <div style={{ backgroundColor: "rgba(30, 30, 30, 0.7)", border: "4px solid gold", borderRadius: 20, padding: "50px", backdropFilter: "blur(10px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: "700px" }}>
        
        {currentUser ? (
          <div style={{ marginBottom: 40, textAlign: "center" }}>
            <p style={{ color: "#aaa", margin: "0 0 5px 0", fontSize: "1.2em", fontWeight: "bold" }}>Playing as</p>
            <h2 style={{ color: "gold", fontSize: "2.5em", margin: 0 }}>{currentUser.displayName}</h2>
            <p style={{ color: "lightgreen", margin: "5px 0 0 0", fontSize: "0.9em" }}>✓ Account verified</p>
          </div>
        ) : (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <input type="text" placeholder="Enter Guest Name..." value={myName} onChange={e => setMyName(e.target.value.replace(/[<>"'`/\\]/g, "").substring(0, 15))} style={{ padding: "15px 20px", fontSize: "1.5em", borderRadius: 12, width: "100%", maxWidth: "400px", border: "2px solid #aaa", backgroundColor: "rgba(10, 10, 10, 0.8)", color: "white", marginBottom: 15, textAlign: "center", outline: "none", fontWeight: "bold" }} />
            <p style={{ color: "#888", marginBottom: 40, fontSize: "0.9em" }}>Or create an account to save your stats!</p>
          </div>
        )}

        <div style={{ width: "100%", marginBottom: "30px" }}>
          <button onClick={() => { const finalName = myName.trim() || `Player ${Math.floor(Math.random() * 1000)}`; socket.emit("create_bot_game", finalName); }} style={{ width: "100%", padding: "18px", fontSize: "1.4em", backgroundColor: "rgba(255, 255, 255, 0.1)", color: "white", border: "2px solid #888", borderRadius: 15, cursor: "pointer", fontWeight: "bold", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)"; e.currentTarget.style.borderColor = "white"; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)"; e.currentTarget.style.borderColor = "#888"; }}>🤖 Quick Play (vs Bot)</button>
        </div>
        
        <div style={{ display: "flex", justifyContent: "center", gap: 30, flexWrap: "wrap", width: "100%" }}>
          <div style={{ backgroundColor: "rgba(20, 20, 20, 0.8)", padding: 30, borderRadius: 15, border: "2px solid #555", flex: 1, minWidth: "250px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <h3 style={{ margin: "0 0 20px 0", fontSize: "1.4em", color: "white" }}>New Game</h3>
            <button onClick={() => { const finalName = myName.trim() || `Player ${Math.floor(Math.random() * 1000)}`; socket.emit("create_room", finalName); }} style={{ padding: "15px", fontSize: "1.2em", backgroundColor: "green", color: "white", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: "bold", boxShadow: "0 5px 15px rgba(0,255,0,0.3)" }}>Host Lobby 👑</button>
          </div>
          
          <div style={{ backgroundColor: "rgba(20, 20, 20, 0.8)", padding: 30, borderRadius: 15, border: "2px solid #555", flex: 1, minWidth: "250px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <h3 style={{ margin: "0 0 20px 0", fontSize: "1.4em", color: "white" }}>Join</h3>
            
            {/* NEU: Wackel-Animation und Fehlerbox für ungültigen Code */}
            <div className={isShakingJoin ? "shake-join" : ""} style={{ borderRadius: 8 }}>
              <input type="text" placeholder="Code (A1B2)" value={joinInput} onChange={e => { setJoinInput(e.target.value.toUpperCase()); setJoinError(""); }} style={{ padding: "12px", fontSize: "1.2em", borderRadius: 8, width: "100%", textAlign: "center", marginBottom: 5, border: isShakingJoin ? "2px solid red" : "1px solid #777", backgroundColor: "#111", color: "white", textTransform: "uppercase", fontWeight: "bold", boxSizing: "border-box" }} />
              <div style={{ minHeight: "20px", color: "#ff4444", fontSize: "0.9em", fontWeight: "bold", marginBottom: "10px" }}>{joinError}</div>
            </div>

            <button onClick={() => { const finalName = myName.trim() || `Player ${Math.floor(Math.random() * 1000)}`; if (joinInput.trim()) { socket.emit("join_room", { roomCode: joinInput, playerName: finalName }); } else { setJoinError("Please enter a code!"); setIsShakingJoin(true); setTimeout(() => setIsShakingJoin(false), 500); } }} style={{ padding: "15px", fontSize: "1.2em", backgroundColor: "gold", color: "black", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: "bold", boxShadow: "0 5px 15px rgba(255,215,0,0.3)" }}>Join ➡️</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLobby = () => {
    const amIHost = lobbyPlayers.length > 0 && lobbyPlayers[0].id === socket.id;
    const hostName = lobbyPlayers.length > 0 ? lobbyPlayers[0].name : "Player";
    return (
      <div style={{ padding: "40px 20px", color: "white", minHeight: "100vh", fontFamily: "sans-serif", backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Helmet><title>⏳ Lobby of ${hostName} | UN-LOCKED</title></Helmet>
        <style>{` button { transition: transform 0.1s ease-in-out, filter 0.1s ease-in-out, box-shadow 0.1s ease-in-out; } button:not(:disabled):hover { transform: scale(1.03); filter: brightness(1.2); } button:disabled { opacity: 0.6; cursor: not-allowed !important; transform: none !important; filter: none !important; } input[type=range] { -webkit-appearance: none; width: 100%; background: transparent; } input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; height: 24px; width: 24px; border-radius: 50%; background: white; cursor: pointer; margin-top: -10px; box-shadow: 0 0 5px rgba(0,0,0,0.5); } input[type=range]::-webkit-slider-runnable-track { width: 100%; height: 4px; cursor: pointer; background: white; border-radius: 2px; } `}</style>
        <h1 style={{ fontSize: "3.5em", margin: "0 0 40px 0", textShadow: "0 4px 10px rgba(0,0,0,0.8)" }}>{hostName}'s Lobby</h1>
        <div style={{ width: "100%", maxWidth: "600px", display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ backgroundColor: "rgba(30, 30, 30, 0.85)", border: "3px solid gold", borderRadius: 15, padding: "30px", textAlign: "center", backdropFilter: "blur(8px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}>
            <h2 style={{ margin: "0 0 5px 0", fontSize: "2.5em" }}>Room Code: <span style={{ color: "gold", letterSpacing: "2px" }}>{roomCode}</span></h2>
            <p style={{ color: "#888", fontSize: "1.1em", margin: "0 0 20px 0", fontWeight: "bold", lineHeight: "1.4" }}>Send the Code to your Friends<br/>or copy this Link to join</p>
            <input readOnly value={`${window.location.origin}/host?code=${roomCode}`} onClick={e => { e.target.select(); document.execCommand("copy"); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }} style={{ width: "90%", padding: "15px", backgroundColor: "rgba(20, 20, 20, 0.9)", color: "white", border: "1px solid #555", borderRadius: "10px", textAlign: "center", cursor: "pointer", fontSize: "1.1em", fontWeight: "bold" }} />
          </div>
          {lobbyPlayers.map((p, index) => (
            <div key={p.id} style={{ backgroundColor: "rgba(30, 30, 30, 0.85)", border: "3px solid gold", borderRadius: 15, padding: "20px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", backdropFilter: "blur(8px)", fontSize: "1.5em", fontWeight: "bold", boxShadow: "0 5px 15px rgba(0,0,0,0.3)" }}>
             <span>{p.name} {p.id === socket.id ? "(You)" : ""}</span>
             {p.isHost && <span style={{ color: "gold" }}>Host</span>}
            </div>
          ))}
          {currentUser && myFriends.length > 0 && (
            <div style={{ backgroundColor: "rgba(20, 20, 20, 0.85)", border: "2px solid #555", borderRadius: 15, padding: "20px", marginTop: "10px", backdropFilter: "blur(8px)", boxShadow: "0 5px 15px rgba(0,0,0,0.3)" }}>
              <h3 style={{ margin: "0 0 15px 0", color: "gold", fontSize: "1.2em", display: "flex", justifyContent: "space-between" }}><span>Invite Friends</span><span style={{ color: "#888", fontSize: "0.8em" }}>{myFriends.filter(f => globalOnlineUsers[f.uid]?.status === "Online").length} Online</span></h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "150px", overflowY: "auto" }}>
                {myFriends.map(friend => {
                  const isOnlineObj = globalOnlineUsers[friend.uid];
                  const isOnline = !!isOnlineObj; const isPlaying = isOnlineObj?.status === "In Game";
                  const isAlreadyInLobby = lobbyPlayers.some(p => p.name === friend.name);
                  if (isAlreadyInLobby) return null;
                  return (
                    <div key={friend.uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px", backgroundColor: "#111", borderRadius: "8px", border: "1px solid #333" }}>
                      <div style={{ fontSize: "1.1em", fontWeight: "bold", display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: isPlaying ? "orange" : isOnline ? "lime" : "gray", boxShadow: `0 0 8px ${isPlaying ? "orange" : isOnline ? "lime" : "transparent"}` }} />
                        {friend.avatar} {friend.name}
                      </div>
                      <button disabled={!isOnline || isPlaying} onClick={() => { socket.emit("invite_friend", { targetUid: friend.uid, roomCode: roomCode, hostName: currentUser.displayName }); alert(`Invite sent to ${friend.name}!`); }} style={{ padding: "6px 12px", backgroundColor: isOnline && !isPlaying ? "green" : "#444", color: "white", border: "none", borderRadius: "5px", cursor: isOnline && !isPlaying ? "pointer" : "not-allowed", fontWeight: "bold", fontSize: "0.9em" }}>
                        {isPlaying ? "In Game" : isOnline ? "Invite ➕" : "Offline"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {amIHost && (
            <div style={{ backgroundColor: "rgba(30, 30, 30, 0.85)", border: "3px solid gold", borderRadius: 15, padding: "25px 30px", backdropFilter: "blur(8px)", display: "flex", flexDirection: "column", gap: "25px", boxShadow: "0 5px 15px rgba(0,0,0,0.3)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1.4em", fontWeight: "bold" }}>
                <span style={{ width: "220px" }}>Rounds (3-50):</span>
                <div style={{ display: "flex", alignItems: "center", flex: 1, position: "relative" }}>
                  <input type="range" min="3" max="50" value={lobbyTotalRounds} onChange={(e) => setLobbyTotalRounds(Number(e.target.value))} />
                  <span style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", backgroundColor: "white", color: "black", borderRadius: "50%", width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "0.7em", pointerEvents: "none", boxShadow: "0 0 5px rgba(0,0,0,0.5)" }}>{lobbyTotalRounds}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1.4em", fontWeight: "bold" }}>
                <span style={{ width: "220px" }}>Max Players (2-10):</span>
                <div style={{ display: "flex", alignItems: "center", flex: 1, position: "relative" }}>
                  <input type="range" min="2" max="10" value={lobbyMaxPlayers} onChange={(e) => { const newVal = Number(e.target.value); setLobbyMaxPlayers(newVal); socket.emit("update_lobby_settings", { roomCode, maxPlayers: newVal }); }} />
                  <span style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", backgroundColor: "white", color: "black", borderRadius: "50%", width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "0.7em", pointerEvents: "none", boxShadow: "0 0 5px rgba(0,0,0,0.5)" }}>{lobbyMaxPlayers}</span>
                </div>
              </div>
            </div>
          )}
          {amIHost ? (
            <button onClick={() => socket.emit("start_game", { roomCode, totalRounds: lobbyTotalRounds || 25, maxPlayers: lobbyMaxPlayers })} disabled={lobbyPlayers.length < 2} style={{ backgroundColor: "rgba(30, 30, 30, 0.85)", border: "3px solid gold", borderRadius: 15, padding: "20px", fontSize: "2.2em", color: "white", fontWeight: "bold", cursor: lobbyPlayers.length < 2 ? "not-allowed" : "pointer", backdropFilter: "blur(8px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", marginTop: "10px" }}>Start Game</button>
          ) : (
            <div style={{ textAlign: "center", fontSize: "1.5em", color: "gold", marginTop: "20px", fontWeight: "bold" }}>Waiting for Host to start... </div>
          )}
        </div>
      </div>
    );
  };

  const renderRoundEnd = () => {
    const isReady = playersReady.includes(myPlayer.id);
    const winnerPlayer = players.find(p => p.hand.length === 0);
    return (
      <div style={{ color: "white", minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: 20, backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
        <Helmet><title>Round over | UN-LOCKED</title></Helmet>
        {renderDisconnectOverlay()}
        <style>{` button { transition: transform 0.1s ease-in-out, filter 0.1s ease-in-out, box-shadow 0.1s ease-in-out; } button:not(:disabled):hover { transform: scale(1.05); filter: brightness(1.2); } button:disabled { opacity: 0.5; cursor: not-allowed !important; transform: none !important; filter: none !important; } `}</style>
        <div style={{ backgroundColor: "rgba(30, 30, 30, 0.8)", border: "4px solid gold", borderRadius: 20, padding: "50px", backdropFilter: "blur(10px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", maxWidth: "800px", width: "100%" }}>
          <h1 style={{ color: "gold", fontSize: "4em", margin: "0 0 10px 0", textShadow: "0 4px 10px rgba(0,0,0,0.8)" }}> ROUND OVER </h1>
          <h2 style={{ fontSize: "2.5em", marginBottom: 30 }}>Winner: <span style={{color: "gold"}}>{winnerPlayer ? winnerPlayer.name : "Noone, Draw"}</span></h2>
          <p style={{ color: "#aaa", fontSize: "1.2em", marginBottom: 50, fontWeight: "bold" }}>You will continue to the Shop when all Players are ready!</p>
          <button onClick={() => socket.emit("round_end_ready", { roomCode, playerIndex: myPlayerIndex })} disabled={isReady} style={{ padding: "20px 50px", fontSize: "1.5em", fontWeight: "bold", backgroundColor: isReady ? "rgba(17,17,17,0.9)" : "green", color: isReady ? "green" : "white", border: isReady ? "2px solid green" : "none", borderRadius: 15, cursor: isReady ? "not-allowed" : "pointer", boxShadow: isReady ? "none" : "0 5px 15px rgba(0,255,0,0.3)" }}>
            {isReady ? `Waiting for others... (${playersReady.length}/${players.length})` : "Continue to Shop"}
          </button>
        </div>
      </div>
    );
  };

  const renderGameOver = () => {
    const sortedLeaderboard = [...players].sort((a, b) => { if (b.wins !== a.wins) return b.wins - a.wins; return b.cumulativeHp - a.cumulativeHp; });
    return (
      <div style={{ color: "white", minHeight: "100vh", padding: 50, display: "flex", flexDirection: "column", alignItems: "center", backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
        <Helmet><title>Game Over | UN-LOCKED</title></Helmet>
        {renderDisconnectOverlay()} {renderLeaveConfirm()}
        <style>{` button { transition: transform 0.1s ease-in-out, filter 0.1s ease-in-out, box-shadow 0.1s ease-in-out; } button:not(:disabled):hover { transform: scale(1.05); filter: brightness(1.2); } `}</style>
        <div style={{ backgroundColor: "rgba(30, 30, 30, 0.8)", border: "4px solid gold", borderRadius: 20, padding: "50px", backdropFilter: "blur(10px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", maxWidth: "900px", width: "100%", textAlign: "center" }}>
          <h1 style={{ color: "gold", fontSize: "4.5em", margin: "0 0 20px 0", textShadow: "0 4px 10px rgba(0,0,0,0.8)" }}> RESULTS </h1>
          <table style={{ width: "100%", margin: "40px auto", borderCollapse: "separate", borderSpacing: "0 10px", fontSize: "1.3em" }}>
            <thead>
              <tr style={{ color: "gold", textTransform: "uppercase" }}>
                <th style={{ padding: "0 15px 15px 15px" }}>Place</th><th style={{ paddingBottom: 15 }}>Name</th><th style={{ paddingBottom: 15 }}>Wins 🏆</th><th style={{ paddingBottom: 15 }}>Total HP ❤️</th><th style={{ paddingBottom: 15 }}>Gold 🪙</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeaderboard.map((p, index) => (
                <tr key={p.id} style={{ backgroundColor: index === 0 ? "rgba(255,215,0,0.2)" : "rgba(20,20,20,0.8)" }}>
                  <td style={{ padding: "15px", borderRadius: "10px 0 0 10px", border: index === 0 ? "2px solid gold" : "2px solid #444", borderRight: "none", fontWeight: "bold" }}>{index + 1}.</td>
                  <td style={{ borderTop: index === 0 ? "2px solid gold" : "2px solid #444", borderBottom: index === 0 ? "2px solid gold" : "2px solid #444", fontWeight: index === 0 ? "bold" : "normal", color: index === 0 ? "gold" : "white" }}>{p.name} {index === 0 && "👑"}</td>
                  <td style={{ borderTop: index === 0 ? "2px solid gold" : "2px solid #444", borderBottom: index === 0 ? "2px solid gold" : "2px solid #444" }}>{p.wins}</td>
                  <td style={{ borderTop: index === 0 ? "2px solid gold" : "2px solid #444", borderBottom: index === 0 ? "2px solid gold" : "2px solid #444" }}>{p.cumulativeHp}</td>
                  <td style={{ padding: "15px", borderRadius: "0 10px 10px 0", border: index === 0 ? "2px solid gold" : "2px solid #444", borderLeft: "none" }}>{p.cumulativeGold}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => { trackStat("doubloons", 1); setShowLeaveConfirm(true); }} style={{ marginTop: "20px", padding: "15px 40px", fontSize: "1.2em", backgroundColor: "gold", color: "black", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: "bold", boxShadow: "0 5px 15px rgba(255,215,0,0.3)" }}>Back to Main Menu</button>
        </div>
      </div>
    );
  };


  // ==========================================
  // 5. HAUPT-ROUTER (DIE MAGISCHE WEICHE)
  // ==========================================

  if (appView === "setup") return renderSetup();
  if (appView === "lobby_waiting") return renderLobby();

  // Ab hier brauchen wir Spieldaten
  if (!players || players.length === 0 || !topCard || !myPlayer) {
    return <div style={{ color: "white", minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}><h2>Lade Spieldaten... ⏳</h2></div>;
  }

  if (gamePhase === "start_draft") return <><Draft socket={socket} roomCode={roomCode} gamePhase={gamePhase} playersReady={playersReady} myPlayer={myPlayer} myPlayerIndex={myPlayerIndex} players={players} />{renderDisconnectOverlay()}</>;
  if (gamePhase === "round_end") return renderRoundEnd();
  if (gamePhase === "game_over") return renderGameOver();
  if (gamePhase === "shop") return <><Helmet><title> Shop | UN-LOCKED</title></Helmet>{renderDisconnectOverlay()}<Shop socket={socket} roomCode={roomCode} gamePhase={gamePhase} playersReady={playersReady} myPlayer={myPlayer} myPlayerIndex={myPlayerIndex} currentShop={currentShop} openedPackage={openedPackage} setOpenedPackage={setOpenedPackage} currentRound={currentRound} players={players} /></>;

  // DAS BATTLE BEREICH (Der Rest vom Code bleibt hier, weil er am größten ist)
  const isMyTurn = activePlayer?.id === myPlayer?.id;
  
  // --- NEU: Prüfen, ob der Spieler die Architect-Boni hat ---
  const architectCount = myPlayer?.activeEffects?.filter(e => e.family === "Architect").length || 0;
  const hasScry = architectCount >= 3;
  const hasRecycle = architectCount >= 5;

  return (
    <div className={`${screenShake ? "shake-active" : ""} ${screenFlash ? `flash-${screenFlash}` : ""}`} 
         style={{ padding: 20, position: "relative", minHeight: "100vh", transition: "box-shadow 0.2s", backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <Helmet><title>⚔️ Battle | UN-LOCKED</title></Helmet>
      {renderLeaveConfirm()}
      {renderDisconnectOverlay()}

      <style>{`
        button { transition: transform 0.1s ease-in-out, filter 0.1s ease-in-out; }
        button:not(:disabled):hover { transform: scale(1.05); filter: brightness(1.2); }
        button:disabled { opacity: 0.6; cursor: not-allowed !important; transform: none !important; filter: none !important; }

        .enemy-scroll::-webkit-scrollbar { height: 8px; }
        .enemy-scroll::-webkit-scrollbar-thumb { background: #555; border-radius: 4px; }
        
        .hand-card-wrapper { transition: transform 0.2s ease, margin 0.2s ease; margin-left: -60px; position: relative; }
        .hand-card-wrapper:first-child { margin-left: 0; }
        .hand-card-wrapper:hover { transform: translateY(-25px) scale(1.1); z-index: 100 !important; margin-right: 15px; }
        
        .shake-card { animation: shake-anim 0.4s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake-anim {
          10%, 90% { transform: translate3d(-2px, 0, 0); } 20%, 80% { transform: translate3d(3px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-5px, 0, 0); } 40%, 60% { transform: translate3d(5px, 0, 0); }
        }
        
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        .slide-left-anim { animation: slide-in-left 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        .slide-right-anim { animation: slide-in-right 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        
        /* NEU: Pulsierender Rahmen für den Recycle-Modus */
        @keyframes pulse-blue { 0% { box-shadow: 0 0 10px #2196F3; } 50% { box-shadow: 0 0 25px #2196F3; } 100% { box-shadow: 0 0 10px #2196F3; } }
        .recycle-glow { animation: pulse-blue 1.5s infinite; border-radius: 8px; }
      `}</style>

      <div style={{ position: "absolute", top: 20, right: 20, display: "flex", gap: 15, zIndex: 99 }}>
        <button onClick={() => { trackStat("doubloons", 1); setShowLeaveConfirm(true); }} style={{ backgroundColor: "#222", color: "white", fontWeight: "bold", padding: "10px 15px", cursor: "pointer", border: "2px solid #555", borderRadius: 8 }}>LEAVE GAME</button>
      </div>

      <div className="enemy-scroll" style={{ display: "flex", gap: "15px", overflowX: "auto", paddingBottom: "15px", paddingTop: "60px", width: "100%", marginBottom: "20px", marginTop: "-40px" }}>
        {players.map((enemy, originalIndex) => {
          if (enemy.id === myPlayer.id) return null; 
          const isUndercover = enemy.activeEffects?.some(e => e.name === "Undercover");

          return (
            <div key={enemy.id} style={{ position: "relative", minWidth: "220px", backgroundColor: "rgba(26, 26, 26, 0.85)", border: "2px solid #333", borderRadius: "10px", padding: "15px", opacity: enemy.hp <= 0 ? 0.5 : 1, backdropFilter: "blur(5px)" }}>
              {enemy.id === activePlayer.id && (
                <motion.div
                  layoutId="turnIndicator"
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  style={{ position: "absolute", top: -4, left: -4, right: -4, bottom: -4, border: "3px solid gold", borderRadius: "12px", boxShadow: "0 0 20px rgba(255, 215, 0, 0.6)", zIndex: -1, pointerEvents: "none" }}
                />
              )}
              {chatBubbles[originalIndex] && (
                <div style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", backgroundColor: "white", color: "black", padding: "8px 12px", borderRadius: "12px", fontWeight: "bold", fontSize: "1.1em", whiteSpace: "nowrap", zIndex: 100, boxShadow: "0 4px 10px rgba(0,0,0,0.5)", animation: "popIn 0.2s ease-out", marginBottom: "12px" }}>
                  {chatBubbles[originalIndex]}
                  <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", borderWidth: "6px", borderStyle: "solid", borderColor: "white transparent transparent transparent" }} />
                </div>
              )}
              <h3 style={{ margin: "0 0 10px 0", color: enemy.id === activePlayer.id ? "gold" : "white", display: "flex", justifyContent: "space-between" }}>
                <span>{enemy.name} {isUndercover ? "🕵️" : ""}</span><span>{enemy.id === activePlayer.id ? "🎯" : ""}</span>
              </h3>
              <div style={{ backgroundColor: "rgba(0,0,0,0.4)", padding: "10px", borderRadius: "8px", color: isUndercover ? "gray" : "#ddd", fontSize: "0.9em", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div>❤️ {isUndercover ? "???" : enemy.hp}</div><div>🪙 {isUndercover ? "???" : enemy.gold}</div><div>🃏 {isUndercover ? "???" : enemy.drawPile.length} Deck</div><div>🎴 {isUndercover ? "???" : enemy.hand.length} Hand</div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 30 }}>
        <div style={{ padding: "20px 40px", backgroundColor: "rgba(34, 34, 34, 0.85)", color: "white", borderRadius: 15, position: "relative", minWidth: 280, textAlign: "center", border: "4px solid #333", display: "flex", flexDirection: "column", alignItems: "center", backdropFilter: "blur(5px)", transition: "width 0.3s ease" }}>
          <h4 style={{ margin: "0 0 25px 0", fontSize: "1.2em", color: "#ddd" }}>Table | Round {currentRound}/{totalRounds} | Direction: {direction === 1 ? "➡️" : "⬅️"}</h4>
          <div style={{ display: "flex", alignItems: "center", gap: "40px", justifyContent: "center" }}>
            
            {(chooseColorMode || showSniperOverlay || showWishOverlay) && (
              <div className="slide-left-anim" style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
                <h3 style={{ margin: "0 0 5px 0", color: showSniperOverlay ? "red" : "white", fontSize: "1.1em", textTransform: "uppercase" }}>{chooseColorMode ? "Choose a Color" : showSniperOverlay ? " Select Target" : "Wish Color"}</h3>
                {(chooseColorMode || showWishOverlay) && [ { id: "blue", label: "Blue", hex: "#0088ff" }, { id: "red", label: "Red", hex: "#ff0000" }, { id: "green", label: "Green", hex: "#00cc00" }, { id: "yellow", label: "Yellow", hex: "#ffee00" } ].map(c => {
                  const isActive = showWishOverlay && wishColor === c.id;
                  return (
                    <button key={c.id} onClick={() => { if (chooseColorMode) { socket.emit("choose_color", { roomCode, color: c.id, playerIndex: myPlayerIndex }); setChooseColorMode(false); } else { setWishColor(c.id); } }} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 15px", backgroundColor: isActive ? "#444" : "rgba(0,0,0,0.5)", border: `2px solid ${isActive ? c.hex : "#444"}`, borderRadius: "10px", color: "white", fontWeight: "bold", fontSize: "1.1em", cursor: "pointer", width: "150px" }}>
                      <div style={{ width: "20px", height: "20px", borderRadius: "50%", backgroundColor: c.hex, boxShadow: `0 0 8px ${c.hex}` }} />{c.label}
                    </button>
                  );
                })}
                {showSniperOverlay && players.filter(p => p.id !== myPlayer.id).map(target => (
                  <button key={target.id} onClick={() => { socket.emit("sniper_fire", { roomCode, targetId: target.id }); setShowSniperOverlay(false); }} style={{ padding: "10px 15px", fontSize: "1.1em", backgroundColor: "rgba(0,0,0,0.5)", color: "white", border: "2px solid darkred", borderRadius: 10, cursor: "pointer", fontWeight: "bold", width: "150px", textAlign: "left" }}>🎯 {target.name}</button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: "40px", alignItems: "center", justifyContent: "center", zIndex: 10, flexWrap: "wrap", paddingBottom: "20px" }}>
        
        {/* --- LINKER BEREICH: DIE ZWEI ABLEGESTAPEL --- */}
        <div style={{ display: "flex", gap: "30px", alignItems: "flex-end", position: "relative" }}>
          
          {activePenalty > 0 && (
            <div style={{ position: "absolute", top: -40, left: "50%", transform: "translateX(-50%)", backgroundColor: "red", color: "white", padding: "8px 15px", borderRadius: 20, fontWeight: "bold", boxShadow: "0 0 15px red", zIndex: 20 }}>
              +{activePenalty} PENALTY!
            </div>
          )}

          {/* 1. NORMAL PILE */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
            <span style={{ color: "#aaa", fontWeight: "bold", fontSize: "0.85em", letterSpacing: "2px" }}>NORMAL PILE</span>
            <div style={{ position: "relative", width: 120, height: 180 }}>
              <AnimatePresence>
                {cardHistory.map((card, idx) => {
                  const isTop = idx === cardHistory.length - 1;
                  const rotation = idx === 0 ? -6 : idx === 1 ? 4 : 0;
                  const offsetX = idx === 0 ? -10 : idx === 1 ? 8 : 0;
                  const offsetY = idx === 0 ? 5 : idx === 1 ? -3 : 0;

                  return (
                    <motion.div
                      key={card.id + "-" + idx}
                      initial={{ scale: 1.5, opacity: 0, y: -150, rotate: rotation - 20 }}
                      animate={{ scale: 1, opacity: 1, y: offsetY, x: offsetX, rotate: rotation }}
                      transition={{ type: "spring", stiffness: 350, damping: 25 }}
                      style={{ position: "absolute", top: 0, left: 0, zIndex: idx + 1, filter: isTop ? "none" : `brightness(${0.5 + (idx * 0.2)})`, boxShadow: isTop ? "0px 10px 25px rgba(0,0,0,0.8)" : "none" }}
                    >
                      <Card card={card} disabled={true} />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>

          {/* 2. SPECIAL PILE */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
            <span style={{ color: "gold", fontWeight: "bold", fontSize: "0.85em", letterSpacing: "2px" }}>SPECIAL PILE</span>
            <div style={{ position: "relative", width: 120, height: 180, border: "2px dashed #555", borderRadius: "10px", backgroundColor: "rgba(0,0,0,0.3)" }}>
              {topSpecialCard ? (
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ position: "absolute", top: 0, left: 0, zIndex: 3, boxShadow: "0px 0px 30px rgba(255,215,0,0.4)" }}>
                  <Card card={topSpecialCard} disabled={true} />
                </motion.div>
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", color: "#555", fontWeight: "bold" }}>Empty</div>
              )}
            </div>
          </div>
        </div>

        {/* --- RECHTER BEREICH: AKTIONEN & OVERLAYS --- */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", width: "160px" }}>
          
          <div style={{ width: "100%" }}>
            {(chooseColorMode || showSniperOverlay) ? (
              <div style={{ color: showSniperOverlay ? "red" : "gold", fontWeight: "bold", fontSize: "1.1em", animation: "pulse 1.5s infinite", textAlign: "center" }}>Choose an Option!</div>
            ) : showWishOverlay ? (
              <button onClick={() => { socket.emit("wish_fulfilled", { roomCode, color: wishColor, value: wishNumber }); setShowWishOverlay(false); }} style={{ width: "100%", padding: "12px", fontSize: "1.1em", backgroundColor: "gold", color: "black", border: "2px solid #b8860b", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", boxShadow: "0 0 15px rgba(255, 215, 0, 0.5)" }}>Confirm</button>
            ) : (
              <>
                {activePenalty > 0 ? ( 
                  <button disabled={!isMyTurn || gameOver} onClick={drawCard} style={{ width: "100%", padding: "12px", fontSize: "1.1em", backgroundColor: "darkred", color: "white", border: "2px solid red", borderRadius: "8px", cursor: isMyTurn ? "pointer" : "not-allowed", fontWeight: "bold", opacity: isMyTurn ? 1 : 0.5 }}>Penalty (+{activePenalty})</button>
                ) : hasDrawn ? ( 
                  <button disabled={!isMyTurn || gameOver} onClick={endTurn} style={{ width: "100%", padding: "12px", fontSize: "1.1em", backgroundColor: "#2e8b57", color: "white", border: "2px solid #555", borderRadius: "8px", cursor: isMyTurn ? "pointer" : "not-allowed", fontWeight: "bold", opacity: isMyTurn ? 1 : 0.5 }}>End Turn</button>
                ) : ( 
                  <button disabled={!isMyTurn || gameOver} onClick={drawCard} style={{ width: "100%", padding: "12px", fontSize: "1.1em", backgroundColor: "#444", color: "white", border: "2px solid #666", borderRadius: "8px", cursor: isMyTurn ? "pointer" : "not-allowed", opacity: isMyTurn ? 1 : 0.5, fontWeight: "bold" }}>Draw Card {hasScry && "🔮"}</button> 
                )}

                {/* --- NEU: ARCHITECT RECYCLE BUTTON (Tier 5) --- */}
                {hasRecycle && isMyTurn && !hasDrawn && activePenalty === 0 && (
                  <button 
                    onClick={() => setRecycleMode(!recycleMode)} 
                    style={{ width: "100%", padding: "10px", marginTop: "10px", fontSize: "1em", backgroundColor: recycleMode ? "#2196F3" : "transparent", color: recycleMode ? "white" : "#2196F3", border: "2px solid #2196F3", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", transition: "all 0.2s" }}
                  >
                    {recycleMode ? "❌ Cancel" : "♻️ Recycle"}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Wish Number Grid */}
          {showWishOverlay && (
            <div className="slide-right-anim" style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
              <h3 style={{ margin: "0 0 5px 0", color: "white", fontSize: "1.1em", textTransform: "uppercase" }}>Wish Number</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(num => (
                  <button key={num} onClick={() => setWishNumber(num)} style={{ width: "45px", height: "45px", backgroundColor: wishNumber === num ? "gold" : "rgba(0,0,0,0.5)", color: wishNumber === num ? "black" : "white", border: `2px solid ${wishNumber === num ? "#fff" : "#555"}`, borderRadius: "10px", fontWeight: "bold", fontSize: "1.3em", cursor: "pointer", gridColumn: num === 0 ? "2" : "auto" }}>{num}</button>
                ))}
              </div>
            </div>
          )}

          </div>
          </div>

          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "stretch", height: "320px" }}>
        <div style={{ width: "180px", backgroundColor: "rgba(17, 17, 17, 0.85)", border: "2px solid #333", borderRadius: 10, padding: "15px", display: "flex", flexDirection: "column", backdropFilter: "blur(5px)", overflowY: "auto" }}>
          <div onClick={() => setShowEffects(true)} title="Click to see Details about your Effects." style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginBottom: "15px", padding: "8px", borderRadius: "8px", cursor: "pointer", transition: "background 0.2s ease" }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 215, 0, 0.1)"} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
            <h4 style={{ margin: 0, color: "gold", fontSize: "1.1em" }}>Your Effects</h4><span style={{ fontSize: "0.9em", filter: "brightness(2) grayscale(1)" }}>🔍</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" }}>
            {myPlayer.activeEffects?.length === 0 && <div style={{color: "gray", fontSize: "0.8em", textAlign: "center", marginTop: 20}}>Keine Effekte aktiv</div>}
            
            {myPlayer.activeEffects?.map(effect => {
              const isUsed = myPlayer.usedEffectsThisRound.includes(effect.id);
              const isActiveType = ["Spy", "Bomb"].includes(effect.name);
              const canBeClicked = isActiveType && !isUsed;
              
              return (
                <div 
                  key={effect.id} 
                  title={canBeClicked ? "Click to Activate" : ""} 
                  style={{ position: "relative", opacity: isUsed ? 0.4 : 1, width: "65px", transition: "transform 0.2s, box-shadow 0.2s", cursor: canBeClicked ? "pointer" : "default", boxShadow: canBeClicked ? "0 0 15px rgba(0, 255, 0, 0.4)" : "none", borderRadius: "8px" }} 
                  onMouseEnter={(e) => { if(canBeClicked) e.currentTarget.style.transform = "translateY(-5px) scale(1.05)"; }} 
                  onMouseLeave={(e) => { if(canBeClicked) e.currentTarget.style.transform = "scale(1.0)"; }}
                  onClick={() => { 
                    if (canBeClicked) { 
                      confetti({ particleCount: 70, spread: 100, origin: { y: 0.9 }, colors: ['#ff00ff', '#00ffff', '#ffd700'], startVelocity: 45 });
                      
                      if(effect.name === "Spy") { 
                        setSpyTargetEffectId(effect.id); // <--- Overlay öffnen statt window.prompt
                      } else { 
                        socket.emit("use_active_effect", { roomCode, playerIndex: myPlayerIndex, effectId: effect.id }); 
                      } 
                    } 
                  }}
                >
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setSellEffectConfirm(effect); // <--- Overlay öffnen statt window.confirm
                    }}
                    style={{ position: "absolute", top: -8, right: -8, zIndex: 10, width: 20, height: 20, backgroundColor: "red", color: "white", border: "1px solid white", borderRadius: "50%", cursor: "pointer", fontWeight: "bold", display: "flex", justifyContent: "center", alignItems: "center", padding: 0, fontSize: "9px" }} 
                    title="Sell for 5 Gold"
                  >✖</button>
                  <Card card={effect} disabled={true} width={65} height={95} />
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, color: "white", backgroundColor: "rgba(17, 17, 17, 0.85)", padding: "10px 15px", borderRadius: 10, backdropFilter: "blur(5px)", zIndex: 999 }}>
            {isMyTurn && (
              <motion.div layoutId="turnIndicator" transition={{ type: "spring", stiffness: 300, damping: 25 }} style={{ position: "absolute", top: -4, left: -4, right: -4, bottom: -4, border: "3px solid gold", borderRadius: "12px", boxShadow: "0 0 20px rgba(255, 215, 0, 0.6)", zIndex: -1, pointerEvents: "none" }} />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h3 style={{ margin: 0, color: isMyTurn ? "lightgreen" : "gray", fontSize: "1.1em" }}>{myPlayer.name} {isMyTurn ? "(Your turn)" : "(Waiting...)"}</h3>
              {chatBubbles[myPlayerIndex] && ( <span style={{ backgroundColor: "white", color: "black", padding: "2px 8px", borderRadius: "10px", fontSize: "0.8em", fontWeight: "bold", animation: "popIn 0.2s" }}>💬 {chatBubbles[myPlayerIndex]}</span> )}
            </div>

            <div style={{ textAlign: "right", display: "flex", alignItems: "center" }}>
                <span style={{ margin: "0 15px 0 0", fontWeight: "bold" }}>❤️ {myPlayer.hp}{myPlayer.maxHp ? ` / ${myPlayer.maxHp}` : ""} | 🃏 {myPlayer.drawPile.length} | 🪙 {myPlayer.gold}</span>
              <button onClick={() => setSortBy(sortBy === "color" ? "none" : "color")} style={{ padding: "4px 8px", cursor: "pointer", backgroundColor: sortBy === "color" ? "gold" : "#333", color: sortBy === "color" ? "black" : "white", border: "1px solid gold", borderRadius: 5, fontWeight: "bold", fontSize: "0.85em" }}>Color</button>
              <button onClick={() => setSortBy(sortBy === "value" ? "none" : "value")} style={{ padding: "4px 8px", cursor: "pointer", marginLeft: 5, backgroundColor: sortBy === "value" ? "gold" : "#333", color: sortBy === "value" ? "black" : "white", border: "1px solid gold", borderRadius: 5, fontWeight: "bold", fontSize: "0.85em" }}>Number</button>
              <button onClick={() => setShowDeck(true)} style={{ padding: "4px 8px", cursor: "pointer", marginLeft: 15, backgroundColor: "#eee", color: "black", border: "1px solid black", borderRadius: 5, fontWeight: "bold", fontSize: "0.85em" }}>🔍 Deck</button>
              
              {currentUser && (
                <div style={{ position: "relative", marginLeft: 15, zIndex: 50 }}>
                  <button onClick={() => setShowEmoteMenu(!showEmoteMenu)} style={{ padding: "4px 8px", cursor: "pointer", backgroundColor: showEmoteMenu ? "gold" : "#222", color: showEmoteMenu ? "black" : "white", border: "1px solid gold", borderRadius: 5, fontSize: "1em", transition: "all 0.2s" }} title="Send Emote">💬</button>
                  {showEmoteMenu && (
                    <div style={{ position: "absolute", top: "100%", right: 0, marginTop: "10px", backgroundColor: "rgba(20,20,20,0.95)", border: "2px solid gold", borderRadius: "10px", padding: "10px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", zIndex: 9999, boxShadow: "0 10px 20px rgba(0,0,0,0.5)", width: "180px" }}>
                      {availableEmotes.map(emote => (
                        <button key={emote} onClick={() => { socket.emit("send_emote", { roomCode, playerIndex: myPlayerIndex, emote }); setShowEmoteMenu(false); }} style={{ padding: "8px 5px", backgroundColor: "#333", color: "white", border: "1px solid #555", borderRadius: "5px", cursor: "pointer", fontWeight: "bold", fontSize: "0.9em" }} onMouseEnter={e => e.target.style.backgroundColor = "gold"} onMouseLeave={e => e.target.style.backgroundColor = "#333"}>{emote}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", padding: "20px", backgroundColor: "rgba(0,0,0,0.5)", borderRadius: "15px", flex: 1, overflowX: "hidden", border: "2px solid #333", backdropFilter: "blur(2px)" }}>
            {(() => {
              let displayHand = [...myPlayer.hand];
              if (sortBy === "color") {
                displayHand.sort((a, b) => (a.color || "").localeCompare(b.color || "") || (a.value || 0) - (b.value || 0));
              } else if (sortBy === "value") {
                displayHand.sort((a, b) => { let vA = a.value !== null ? a.value : 99; let vB = b.value !== null ? b.value : 99; return vA - vB || (a.color || "").localeCompare(b.color || ""); });
              }
              return (
                <AnimatePresence>
                  {displayHand.map((card, index) => {
                    const isPlayable = isMyTurn && checkIfPlayable(card);
                    let animClass = "";
                    if (animatingCardIndex === index) animClass = animationType === "shake" ? "shake-card" : "";

                    return (
                      <motion.div
                        layout
                        key={card.id}
                        initial={{ opacity: 0, y: 150, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -200, scale: 0.5, rotate: 15 }} 
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        className={`hand-card-wrapper ${animClass} ${recycleMode ? "recycle-glow" : ""}`}
                        style={{ zIndex: index }}
                        onClick={() => {
                          if (!isMyTurn || gameOver || chooseColorMode) return;
                          
                          // --- NEU: RECYCLE LOGIK (Tier 5) ---
                          if (recycleMode) {
                            socket.emit("recycle_card", { roomCode, playerIndex: myPlayerIndex, cardId: card.id });
                            setRecycleMode(false);
                            return;
                          }

                          if (!isPlayable) {
                            setAnimationType("shake");
                            setAnimatingCardIndex(index);
                            setTimeout(() => setAnimatingCardIndex(null), 400);
                          } else {
                            const realIndex = myPlayer.hand.findIndex(c => c.id === card.id);
                            playCard(card, realIndex);
                          }
                        }}
                      >
                        {/* Im Recycle-Modus dunkeln wir Karten ab, die der Spieler hovert, um "Zerstörung" zu signalisieren */}
                        <div style={{ transition: "filter 0.2s", filter: recycleMode ? "sepia(0.5) hue-rotate(180deg)" : "none" }}>
                          <Card card={card} disabled={!isMyTurn || gameOver || chooseColorMode} />
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              );
            })()}
          </div>
        </div>

        <div style={{ width: "260px", backgroundColor: "rgba(17, 17, 17, 0.85)", border: "2px solid #333", borderRadius: 10, display: "flex", flexDirection: "column", backdropFilter: "blur(5px)" }}>
          <div style={{ backgroundColor: "rgba(34, 34, 34, 0.9)", padding: "10px", borderRadius: "10px 10px 0 0", borderBottom: "1px solid #444", fontWeight: "bold", color: "gold", textAlign: "center" }}> BATTLE LOG</div>
          <div style={{ padding: "10px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            {actionLog.map((log, i) => (
              <div key={i} style={{ color: i === 0 ? "white" : "#aaa", fontSize: i === 0 ? "0.95em" : "0.85em", opacity: Math.max(0.3, 1 - (i * 0.1)), borderLeft: i === 0 ? "3px solid gold" : "none", paddingLeft: "8px" }}><span style={{color: "gold"}}>[R {log.round}]</span> {log.text}</div>
            ))}
          </div>
        </div>
      </div>

      {/* OVERLAYS BEREICH */}
      {showDeck && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.95)", zIndex: 1000, overflowY: "auto", padding: 50, color: "white" }}>
          <button onClick={() => setShowDeck(false)} style={{ position: "absolute", top: 20, right: 20, padding: "10px 20px", fontSize: "1.2em", cursor: "pointer", backgroundColor: "red", color: "white", border: "none", borderRadius: 5 }}>Cancel ❌</button>
          <h2>Dein Deck ({myPlayer.deck.length} Karten)</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
            {myPlayer.deck.map((card, i) => <Card key={i} card={card} disabled={true} />)}
          </div>
        </div>
      )}

      {showEffects && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.95)", zIndex: 1000, overflowY: "auto", padding: 50, color: "white" }}>
          <button onClick={() => setShowEffects(false)} style={{ position: "absolute", top: 20, right: 20, padding: "10px 20px", fontSize: "1.2em", cursor: "pointer", backgroundColor: "red", color: "white", border: "none", borderRadius: 5 }}>Zurück ❌</button>
          <h2 style={{ color: "gold", fontSize: "2em", textAlign: "center" }}>Your Effects</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginTop: 40, justifyContent: "center" }}>
            {!myPlayer.activeEffects || myPlayer.activeEffects.length === 0 ? ( <h3 style={{ color: "gray" }}>You dont have Effects yet</h3> ) : (
              myPlayer.activeEffects.map((effect, i) => (
                <div key={i} style={{ backgroundColor: "#222", border: "2px solid gold", borderRadius: 10, padding: 20, width: 250, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Card card={effect} disabled={true} /><h3 style={{ color: "gold", marginTop: 15 }}>{effect.name}</h3><p style={{ color: "#aaa" }}>{effect.description}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 👁️ SPION / HACKER ANSICHT */}
      {spyData && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)", zIndex: 9999, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ backgroundColor: "rgba(15,15,15,0.95)", padding: "40px", borderRadius: "20px", border: "2px solid #00ffcc", textAlign: "center", maxWidth: "90%", maxHeight: "90%", overflowY: "auto", boxShadow: "0 0 50px rgba(0, 255, 204, 0.2)" }}>
            <div style={{ fontSize: "3em", marginBottom: "10px", animation: "pulse 2s infinite" }}>👁️‍🗨️</div>
            <h2 style={{ color: "white", marginTop: 0, fontFamily: "monospace", letterSpacing: "2px", textTransform: "uppercase" }}>
              Intercepted Hand: <span style={{ color: "#00ffcc" }}>{spyData.targetName}</span>
            </h2>
            <div style={{ display: "flex", gap: "15px", flexWrap: "wrap", justifyContent: "center", margin: "40px 0" }}>
              {spyData.hand.map((card, i) => (
                <div key={i} style={{ transform: "scale(0.9)", transition: "transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)" }} onMouseEnter={e => e.currentTarget.style.transform="scale(1.1) translateY(-10px)"} onMouseLeave={e => e.currentTarget.style.transform="scale(0.9)"}>
                  <Card card={card} disabled={true} />
                </div>
              ))}
              {spyData.hand.length === 0 && <div style={{ color: "#888", fontStyle: "italic", fontSize: "1.2em" }}>This player has no cards!</div>}
            </div>
            <button onClick={() => setSpyData(null)} style={{ padding: "15px 40px", fontSize: "1.2em", cursor: "pointer", backgroundColor: "transparent", color: "#00ffcc", border: "2px solid #00ffcc", fontWeight: "bold", borderRadius: "10px", transition: "all 0.2s", letterSpacing: "1px" }} onMouseEnter={e => {e.currentTarget.style.backgroundColor="#00ffcc"; e.currentTarget.style.color="black"; e.currentTarget.style.boxShadow="0 0 20px rgba(0,255,204,0.5)";}} onMouseLeave={e => {e.currentTarget.style.backgroundColor="transparent"; e.currentTarget.style.color="#00ffcc"; e.currentTarget.style.boxShadow="none";}}>CLOSE CONNECTION</button>
          </div>
        </div>
      )}

      {/* --- NEU: SCRY OVERLAY (Tier 3 Architect) --- */}
      {scryCards && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.92)", backdropFilter: "blur(15px)", zIndex: 10000, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", animation: "fadeIn 0.3s ease-out" }}>
          <div style={{ fontSize: "4em", marginBottom: "10px", animation: "pulse 2s infinite" }}>🔮</div>
          <h2 style={{ color: "#2196F3", fontSize: "2.5em", textTransform: "uppercase", textShadow: "0 0 15px #2196F3", margin: 0 }}>Scry: Choose your fate</h2>
          <p style={{ color: "#aaa", fontSize: "1.2em", fontStyle: "italic", marginBottom: "40px" }}>The unchosen card will be banished back into your deck.</p>
          
          <div style={{ display: "flex", gap: "50px" }}>
            {scryCards.map((c, i) => (
              <div 
                key={i} 
                onClick={() => { 
                  socket.emit("scry_choice", { roomCode, playerIndex: myPlayerIndex, keptCardId: c.id }); 
                  setScryCards(null); 
                }} 
                style={{ cursor: "pointer", transform: "scale(1.2)", transition: "transform 0.2s, filter 0.2s", filter: "drop-shadow(0 0 10px rgba(33, 150, 243, 0.3))" }} 
                onMouseEnter={e => { e.currentTarget.style.transform="scale(1.3) translateY(-10px)"; e.currentTarget.style.filter="drop-shadow(0 0 25px rgba(33, 150, 243, 0.8))"; }} 
                onMouseLeave={e => { e.currentTarget.style.transform="scale(1.2)"; e.currentTarget.style.filter="drop-shadow(0 0 10px rgba(33, 150, 243, 0.3))"; }}
              >
                <Card card={c} disabled={true} />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* 🎯 OVERLAY: SPION ZIEL AUSWÄHLEN */}
      {spyTargetEffectId && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(5px)", zIndex: 10000, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <h2 style={{ color: "#00ffcc", fontSize: "2.5em", textShadow: "0 0 15px #00ffcc" }}>Select Target to Spy</h2>
          <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
            {players.filter(p => p.id !== myPlayer.id).map(target => (
              <button 
                key={target.id}
                onClick={() => { 
                  socket.emit("use_active_effect", { roomCode, playerIndex: myPlayerIndex, effectId: spyTargetEffectId, targetId: target.id }); 
                  setSpyTargetEffectId(null); 
                }} 
                style={{ padding: "15px 30px", fontSize: "1.2em", backgroundColor: "rgba(0,255,204,0.1)", color: "white", border: "2px solid #00ffcc", borderRadius: "10px", cursor: "pointer", fontWeight: "bold" }}
              >
                👁️ {target.name}
              </button>
            ))}
          </div>
          <button onClick={() => setSpyTargetEffectId(null)} style={{ marginTop: "40px", padding: "10px 20px", backgroundColor: "transparent", color: "gray", border: "none", cursor: "pointer", fontSize: "1.2em" }}>Cancel</button>
        </div>
      )}

      {/* 💰 OVERLAY: EFFEKT VERKAUFEN */}
      {sellEffectConfirm && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(5px)", zIndex: 10000, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <h2 style={{ color: "white", fontSize: "2.5em" }}>Sell <span style={{ color: "gold" }}>{sellEffectConfirm.name}</span>?</h2>
          <p style={{ color: "#aaa", fontSize: "1.2em", marginBottom: "30px" }}>You will receive 5 Gold.</p>
          <div style={{ display: "flex", gap: "20px" }}>
            <button 
              onClick={() => { 
                socket.emit("sell_effect", { roomCode, playerIndex: myPlayerIndex, effectId: sellEffectConfirm.id }); 
                setSellEffectConfirm(null); 
              }} 
              style={{ padding: "15px 40px", fontSize: "1.2em", backgroundColor: "green", color: "white", border: "2px solid lightgreen", borderRadius: "10px", cursor: "pointer", fontWeight: "bold" }}
            >
              ✅ Confirm
            </button>
            <button onClick={() => setSellEffectConfirm(null)} style={{ padding: "15px 40px", fontSize: "1.2em", backgroundColor: "darkred", color: "white", border: "2px solid red", borderRadius: "10px", cursor: "pointer", fontWeight: "bold" }}>❌ Cancel</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes smooth-hit { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-10px); } 40% { transform: translateX(10px); } 60% { transform: translateX(-5px); } 80% { transform: translateX(5px); } }
        .shake-active { animation: smooth-hit 0.4s ease-in-out both; }
        .flash-damage { box-shadow: inset 0 0 100px rgba(255, 0, 0, 0.6); transition: box-shadow 0.1s ease-out; }
        .flash-heal { box-shadow: inset 0 0 100px rgba(0, 255, 0, 0.6); transition: box-shadow 0.1s ease-out; }
        .flash-gold { box-shadow: inset 0 0 100px rgba(255, 215, 0, 0.6); transition: box-shadow 0.1s ease-out; }
      `}</style>
    </div>
  );
}